/**
 * Face-to-Voice Generation Service
 *
 * Uses Supabase Edge Function to analyze character facial features and appearance
 * to generate a voice description based on physiognomy principles.
 *
 * The edge function handles image fetching server-side to avoid CORS issues.
 */

import { CharacterArchetype } from './physiognomyVoicePrompt';
import { supabase } from './supabase';


export interface FaceVoiceInput {
  headImageUrl: string;
  bodyImageUrl?: string;
  referenceSheetUrl?: string;
  archetype?: CharacterArchetype;
  characterName?: string;
  characterDescription?: string;
  passportDescription?: string; // Character passport text description for additional context
}

export interface FaceVoiceResult {
  voiceDescription: string;
  success: boolean;
  error?: string;
}

/**
 * Strip markdown formatting from text
 * Removes ** (bold) and ## (headers)
 */
function stripMarkdown(text: string): string {
  return text
    // Remove ** for bold text
    .replace(/\*\*/g, '')
    // Remove ## for headers (at start of lines)
    .replace(/^##\s+/gm, '')
    // Remove single # for headers (at start of lines)
    .replace(/^#\s+/gm, '');
}

/**
 * Generate a voice description from character facial features using Gemini Vision
 *
 * @param input - The character images and metadata
 * @returns A voice description suitable for TTS voice design
 */
export async function generateVoiceFromFace(input: FaceVoiceInput): Promise<FaceVoiceResult> {
  try {
    console.log(`🎭 Generating voice from face for ${input.characterName || 'character'}...`);
    console.log(`   Head image: ${input.headImageUrl.substring(0, 60)}...`);
    if (input.bodyImageUrl) console.log(`   Body image: provided`);
    if (input.referenceSheetUrl) console.log(`   Reference sheet: provided`);

    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('face-to-voice', {
      body: {
        headImageUrl: input.headImageUrl,
        bodyImageUrl: input.bodyImageUrl,
        referenceSheetUrl: input.referenceSheetUrl,
        archetype: input.archetype,
        characterName: input.characterName,
        characterDescription: input.characterDescription,
        passportDescription: input.passportDescription,
      }
    });

    if (error) {
      console.error('Face-to-voice API Error:', error);
      return {
        voiceDescription: '',
        success: false,
        error: error.message || 'Unknown API error'
      };
    }

    if (!data || !data.success || !data.voiceDescription) {
      return {
        voiceDescription: '',
        success: false,
        error: data?.error || 'No voice description generated'
      };
    }

    // Strip markdown formatting from the response
    const cleanedDescription = stripMarkdown(data.voiceDescription);

    console.log('✅ Voice description generated from face:', cleanedDescription);

    return {
      voiceDescription: cleanedDescription,
      success: true
    };

  } catch (error) {
    console.error('Error generating voice from face:', error);
    return {
      voiceDescription: '',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
